import React, { useEffect, useMemo, useState } from 'react';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerTopHeader from '../components/ManagerTopHeader';

import { API_BASE } from '../../config/runtime';

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const formatPeriod = (startDate, endDate) => {
  if (startDate && endDate) return { start: startDate, end: endDate };
  if (startDate) return { start: startDate, end: null };
  if (endDate) return { start: null, end: endDate };
  return { start: null, end: null };
};

/* ─── Style injection ────────────────────────────────────────────────────── */
if (typeof document !== 'undefined' && !document.getElementById('mp-keyframes')) {
  const style = document.createElement('style');
  style.id = 'mp-keyframes';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');

    .mp-fade-up { opacity:0; transform:translateY(14px); animation: mpFadeUp .38s ease forwards; }
    @keyframes mpFadeUp { to { opacity:1; transform:translateY(0); } }

    .mp-input {
      padding: 10px 14px;
      border: 1.5px solid #E2E8F0; border-radius: 10px;
      font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 500;
      background: #FAFBFC; color: #0F172A; outline: none;
      transition: border-color .2s, box-shadow .2s;
    }
    .mp-input:focus { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,.1); background:#fff; }
    .mp-input::placeholder { color: #94A3B8; }

    .mp-select {
      padding: 10px 32px 10px 14px;
      border: 1.5px solid #E2E8F0; border-radius: 10px;
      font-family: 'Sora', sans-serif; font-size: 12px; font-weight: 600;
      background: #FAFBFC; color: #374151; outline: none;
      appearance: none; cursor: pointer;
      background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394A3B8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 10px center;
      transition: border-color .2s, box-shadow .2s;
    }
    .mp-select:focus { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }

    .mp-row {
      display: grid;
      grid-template-columns: 2fr 1.4fr 1fr 100px 2fr;
      gap: 0;
      align-items: center;
      border-bottom: 1px solid #F1F5F9;
      transition: background .15s;
    }
    .mp-row:hover { background: #FAFBFF; }
    .mp-row-cell {
      padding: 16px 14px;
      font-family: 'DM Sans', sans-serif; font-size: 13px; color: #475569;
    }

    /* responsive: collapse to cards on mobile */
    @media (max-width: 700px) {
      .mp-table-header { display: none !important; }
      .mp-row {
        display: flex; flex-direction: column; gap: 0;
        border: 1px solid #E2E8F0; border-radius: 16px;
        margin-bottom: 12px; padding: 0; overflow: hidden;
        box-shadow: 0 2px 8px rgba(15,23,42,.05);
      }
      .mp-row:hover { background: #fff; }
      .mp-row-cell {
        padding: 10px 16px;
        display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;
        border-bottom: 1px solid #F1F5F9;
      }
      .mp-row-cell:last-child { border-bottom: none; }
      .mp-row-cell::before {
        content: attr(data-label);
        font-family: 'Sora', sans-serif; font-size: 10px; font-weight: 700;
        letter-spacing: .08em; text-transform: uppercase; color: #94A3B8;
        white-space: nowrap; flex-shrink: 0; padding-top: 1px;
      }
    }

    .mp-badge {
      display: inline-flex; align-items: center; gap: 5px;
      font-family: 'Sora', sans-serif; font-size: 11px; font-weight: 700;
      letter-spacing: .04em; padding: 4px 10px; border-radius: 999px;
    }
    .mp-badge-live       { background: #DCFCE7; color: #15803D; }
    .mp-badge-scheduled  { background: #FEF9C3; color: #A16207; }
    .mp-badge-ended      { background: #F1F5F9; color: #64748B; }

    .mp-skeleton {
      background: linear-gradient(90deg,#F1F5F9 25%,#E8EDF2 50%,#F1F5F9 75%);
      background-size: 200% 100%;
      animation: shimmer 1.4s infinite; border-radius: 8px;
    }
    @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

    .mp-stat {
      background: #fff; border: 1px solid #E2E8F0; border-radius: 16px;
      padding: 18px 20px; flex: 1; min-width: 120px;
    }
  `;
  document.head.appendChild(style);
}

/* ─── Icons ───────────────────────────────────────────────────────────────── */
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>
);
const IconX = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconTag = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const IconDot = ({ color }) => (
  <svg width="7" height="7" viewBox="0 0 8 8"><circle cx="4" cy="4" r="4" fill={color} /></svg>
);

/* ─── Status badge ────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    Live:      { cls: 'mp-badge mp-badge-live',      dot: '#22C55E' },
    Scheduled: { cls: 'mp-badge mp-badge-scheduled', dot: '#EAB308' },
    Ended:     { cls: 'mp-badge mp-badge-ended',     dot: '#94A3B8' },
  };
  const cfg = map[status] || map.Ended;
  return (
    <span className={cfg.cls}>
      <IconDot color={cfg.dot} />
      {status || 'Unknown'}
    </span>
  );
};

/* ─── Stat card ───────────────────────────────────────────────────────────── */
const StatCard = ({ label, value, accent = '#2563EB' }) => (
  <div className="mp-stat">
    <p style={{ fontFamily: 'Sora, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: '#94A3B8', margin: '0 0 6px' }}>
      {label}
    </p>
    <p style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: 22, color: accent, margin: 0 }}>
      {value}
    </p>
  </div>
);

/* ─── Table skeleton ──────────────────────────────────────────────────────── */
const SkeletonRows = () => (
  <>
    {[...Array(4)].map((_, i) => (
      <div key={i} className="mp-row" style={{ animationDelay: `${i * 60}ms` }}>
        {[140, 100, 80, 70, 200].map((w, j) => (
          <div key={j} className="mp-row-cell">
            <div className="mp-skeleton" style={{ height: 12, width: w, borderRadius: 6 }} />
          </div>
        ))}
      </div>
    ))}
  </>
);

/* ─── Main ────────────────────────────────────────────────────────────────── */
const ManagerPromos = () => {
  const user = useMemo(() => getStoredUser(), []);
  const branchId = user?.branch_id ? Number(user.branch_id) : null;

  const [promos, setPromos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (!branchId) { setErrorMsg('No branch assigned to your account.'); setIsLoading(false); return; }
    (async () => {
      setIsLoading(true); setErrorMsg('');
      try {
        const res = await fetch(`${API_BASE}/get_promos.php`);
        const data = await res.json();
        if (data.success) setPromos(Array.isArray(data.promos) ? data.promos : []);
        else setErrorMsg(data.message || 'Unable to load promotions.');
      } catch (err) {
        setErrorMsg('Unable to load promotions: ' + err.message);
      } finally { setIsLoading(false); }
    })();
  }, [branchId]);

  const filteredPromos = useMemo(() => {
    if (!branchId) return [];
    const term = searchTerm.trim().toLowerCase();
    const fromB = dateFrom ? new Date(dateFrom) : null;
    const toB   = dateTo   ? new Date(dateTo)   : null;

    return promos.filter(p => {
      const branches = Array.isArray(p.branches) ? p.branches : [];
      if (branches.length && !branches.some(id => Number(id) === branchId)) return false;
      if (filterStatus !== 'all' && p.status !== filterStatus) return false;
      if (fromB && p.endDate   && new Date(p.endDate)   < fromB) return false;
      if (toB   && p.startDate && new Date(p.startDate) > toB)   return false;
      if (!term) return true;
      return `${p.name} ${p.promo_code ?? p.code ?? ''} ${p.description ?? ''}`.toLowerCase().includes(term);
    });
  }, [branchId, promos, searchTerm, filterStatus, dateFrom, dateTo]);

  /* stats */
  const stats = useMemo(() => {
    const base = branchId
      ? promos.filter(p => {
          const b = Array.isArray(p.branches) ? p.branches : [];
          return !b.length || b.some(id => Number(id) === branchId);
        })
      : [];
    return {
      total:     base.length,
      live:      base.filter(p => p.status === 'Live').length,
      scheduled: base.filter(p => p.status === 'Scheduled').length,
      ended:     base.filter(p => p.status === 'Ended').length,
    };
  }, [promos, branchId]);

  const hasFilters = searchTerm || filterStatus !== 'all' || dateFrom || dateTo;

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8FAFC', fontFamily: 'DM Sans, sans-serif' }}>
      <ManagerSidebar />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100vh', overflow: 'hidden' }}>
        <ManagerTopHeader pageTitle="Promotions" />

        <div style={{ flex: 1, overflowY: 'auto', padding: 'clamp(16px, 4vw, 40px)' }}>

          {/* ── Page header ── */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: 'clamp(20px, 3vw, 28px)', color: '#0F172A', margin: 0 }}>
              Branch Promotions
            </h1>
            <p style={{ color: '#64748B', fontSize: 13, marginTop: 4 }}>
              View active campaigns and promo codes for your branch.
            </p>
          </div>

          {/* ── Stats row ── */}
          {!isLoading && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }} className="mp-fade-up">
              <StatCard label="Total Promos"  value={stats.total}     accent="#2563EB" />
              <StatCard label="Live"          value={stats.live}      accent="#16A34A" />
              <StatCard label="Scheduled"     value={stats.scheduled} accent="#CA8A04" />
              <StatCard label="Ended"         value={stats.ended}     accent="#94A3B8" />
            </div>
          )}

          {/* ── Error ── */}
          {errorMsg && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '12px 16px', marginBottom: 20, color: '#DC2626', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
              {errorMsg}
            </div>
          )}

          {/* ── Main card ── */}
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 20, overflow: 'hidden', boxShadow: '0 4px 24px rgba(15,23,42,.06)' }}>

            {/* Toolbar */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
              {/* Search */}
              <div style={{ position: 'relative', width: 'clamp(180px, 30vw, 280px)' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', display: 'flex' }}>
                  <IconSearch />
                </span>
                <input
                  className="mp-input"
                  placeholder="Search promos…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: 36, paddingRight: searchTerm ? 36 : 14, width: '100%', boxSizing: 'border-box' }}
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, borderRadius: '50%', background: '#F1F5F9', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
                    <IconX />
                  </button>
                )}
              </div>

              {/* Filters */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Status filter */}
                <select className="mp-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="Live">Live</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Ended">Ended</option>
                </select>

                {/* Date range */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#94A3B8', display: 'flex' }}><IconCalendar /></span>
                  <input
                    type="date" className="mp-input" value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                    style={{ width: 'auto', padding: '8px 10px', fontSize: 12 }}
                  />
                  <span style={{ fontFamily: 'Sora', fontSize: 11, fontWeight: 600, color: '#94A3B8' }}>to</span>
                  <input
                    type="date" className="mp-input" value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                    style={{ width: 'auto', padding: '8px 10px', fontSize: 12 }}
                  />
                </div>

                {/* Clear filters */}
                {hasFilters && (
                  <button
                    onClick={() => { setSearchTerm(''); setFilterStatus('all'); setDateFrom(''); setDateTo(''); }}
                    style={{ fontFamily: 'Sora, sans-serif', fontSize: 11, fontWeight: 700, color: '#64748B', background: '#F1F5F9', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Clear
                  </button>
                )}

                <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 11, fontWeight: 700, color: '#94A3B8', whiteSpace: 'nowrap' }}>
                  {filteredPromos.length} result{filteredPromos.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Table header (hidden on mobile via CSS) */}
            <div className="mp-table-header mp-row" style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
              {['Campaign', 'Period', 'Code', 'Status', 'Details'].map(h => (
                <div key={h} className="mp-row-cell" style={{ fontFamily: 'Sora, sans-serif', fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#94A3B8', paddingTop: 14, paddingBottom: 14 }}>
                  {h}
                </div>
              ))}
            </div>

            {/* Rows */}
            <div>
              {isLoading && <SkeletonRows />}

              {!isLoading && filteredPromos.length === 0 && (
                <div style={{ padding: '60px 24px', textAlign: 'center' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.2" style={{ marginBottom: 16 }}>
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
                  </svg>
                  <p style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#94A3B8', margin: '0 0 6px' }}>
                    No promotions found
                  </p>
                  <p style={{ color: '#CBD5E1', fontSize: 13 }}>
                    {hasFilters ? 'Try adjusting your filters.' : 'No campaigns are active for your branch.'}
                  </p>
                </div>
              )}

              {!isLoading && filteredPromos.map((promo, idx) => {
                const { start, end } = formatPeriod(promo.startDate, promo.endDate);
                const code = promo.promo_code ?? promo.code ?? '—';
                return (
                  <div key={promo.id ?? promo.code ?? idx} className="mp-row mp-fade-up" style={{ animationDelay: `${idx * 40}ms` }}>
                    {/* Campaign */}
                    <div className="mp-row-cell" data-label="Campaign">
                      <span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 13, color: '#0F172A' }}>
                        {promo.name}
                      </span>
                    </div>

                    {/* Period */}
                    <div className="mp-row-cell" data-label="Period">
                      {start || end
                        ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569', fontSize: 12 }}>
                            <IconCalendar />
                            {start && end ? `${start} – ${end}` : start || end}
                          </span>
                        )
                        : <span style={{ color: '#CBD5E1', fontSize: 12 }}>TBD</span>
                      }
                    </div>

                    {/* Code */}
                    <div className="mp-row-cell" data-label="Code">
                      <span style={{
                        fontFamily: 'DM Mono, monospace', fontSize: 12, fontWeight: 500,
                        background: '#EFF6FF', color: '#2563EB',
                        padding: '3px 8px', borderRadius: 6, letterSpacing: '.04em',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        <IconTag />{code}
                      </span>
                    </div>

                    {/* Status */}
                    <div className="mp-row-cell" data-label="Status">
                      <StatusBadge status={promo.status} />
                    </div>

                    {/* Details */}
                    <div className="mp-row-cell" data-label="Details">
                      <span style={{
                        fontSize: 12, color: '#64748B', lineHeight: 1.6,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {promo.description || '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer count */}
            {!isLoading && filteredPromos.length > 0 && (
              <div style={{ padding: '14px 24px', borderTop: '1px solid #F1F5F9', textAlign: 'right' }}>
                <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 11, fontWeight: 600, color: '#CBD5E1' }}>
                  Showing {filteredPromos.length} of {stats.total} promotion{stats.total !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerPromos;
